import React, { useCallback, useState } from 'react';
import type { Nullable, StringMap } from 'corgi-types';
import { QueryParam, SignupFlow } from 'corgi-types';
import type { AuthenticationProvider, ThirdPartyProvider } from 'graphql-ts-types';

import cookieStorage from 'common/CookieStorage';
import { useGQLLocale } from 'common/hooks/locale';
import { useToggleModal } from 'common/hooks/toggle-modal';
import { useLocalization } from 'common/providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { Event } from 'common/types/auth';
import type { GraphQLExtensions } from 'common/types/graphql';
import { extractErrorMessage } from 'common/utils/error-handlers/gql-extensions';
import PasswordModal from '../PasswordModal';
import { getEmailFromJwtToken, getRegistrationUrl, isUserNewUser } from '../utils';
import { executeThirdPartyAssociationQuery, executeThirdPartyLoginQuery } from '../utils/mutation';

interface UserLoginHandlersParams {
  userFlow: SignupFlow;
  setCommonError: (errorMessage: Nullable<string>) => void;
  onSuccess: (args?: { queryParams?: StringMap; homeClubCode?: string | null }) => void;
  appleStateToken?: string;
  appleClientId?: string;
  referredBy?: string;
}

interface ThirdPartyUserData {
  email: string;
  provider: ThirdPartyProvider;
  providerToken: string;
  existentAssociations: AuthenticationProvider[];
}

interface InitModalUserFlowArgs {
  existentAssociations: AuthenticationProvider[];
  provider: ThirdPartyProvider;
  providerToken: string;
}

interface PerformThirdPartyLoginParams {
  setCommonError: (errorMessage: Nullable<string>) => void;
  onSuccess: (args?: { queryParams?: StringMap; homeClubCode?: string | null }) => void;
  userFlow: SignupFlow;
  initModalUserFlow: (args: InitModalUserFlowArgs) => void;
  referredBy?: string;
}

export type PerformLogin = ReturnType<typeof usePerformThirdPartyLogin>;

const usePerformThirdPartyLogin = ({
  setCommonError,
  onSuccess,
  initModalUserFlow,
  userFlow,
  referredBy,
}: PerformThirdPartyLoginParams) => {
  const locale = useGQLLocale();
  const { t, prefix } = useLocalization();

  return useCallback(
    async (provider: ThirdPartyProvider, providerToken: string, providerData?: StringMap) => {
      setCommonError(null);

      try {
        const { data } = await executeThirdPartyLoginQuery({
          locale,
          provider,
          providerToken,
        });

        if ('user' in data) {
          dispatchTrackingEventGA4({
            event: Event.LoginFormClick,
            interaction: 'Submit',
            success: 'Yes',
            provider,
            ...(data.user.visitorId !== null && { visitor_id: data.user.visitorId }),
            ...(data.user.visitorHash !== null && { visitor_hash: data.user.visitorHash }),
            customer_type: data.user.type,
          });
          cookieStorage.setZipCode(data.user.zipCode);
          onSuccess(
            userFlow === SignupFlow.Referral
              ? {
                  queryParams: {
                    [QueryParam.ReferralAccountExists]: '',
                  },
                }
              : {
                  homeClubCode: data.user.homeClub?.code,
                }
          );

          return;
        }

        if ('existentAssociations' in data) {
          const { existentAssociations } = data;

          if (isUserNewUser(existentAssociations)) {
            const redirectUrl = getRegistrationUrl({
              prefix,
              userFlow,
              provider,
              providerToken,
              providerData,
              referredBy,
            });
            window.location.href = redirectUrl;
          } else {
            initModalUserFlow({
              existentAssociations,
              provider,
              providerToken,
            });
          }
        }
      } catch (error) {
        const extensions: GraphQLExtensions = error.extensions || {};
        const message = extractErrorMessage(extensions, t);
        dispatchTrackingEventGA4({
          event: Event.LoginFormClick,
          interaction: 'Submit',
          success: 'No',
          provider,
        });
        setCommonError(message);
      }
    },
    [locale, onSuccess]
  );
};

export const useThirdPartyLoginHandler = ({
  userFlow,
  setCommonError,
  onSuccess,
  appleClientId,
  appleStateToken,
  referredBy,
}: UserLoginHandlersParams) => {
  const { isModalOpen, handleModalOpen, handleModalClose } = useToggleModal();

  const [userData, setUserData] = useState<Nullable<ThirdPartyUserData>>(null);

  const initModalUserFlow = async ({
    existentAssociations,
    provider,
    providerToken,
  }: InitModalUserFlowArgs) => {
    const email = await getEmailFromJwtToken(providerToken);

    setUserData({ existentAssociations, email, provider, providerToken });
    handleModalOpen();
  };

  const onPasswordLoginSuccess = useCallback(async () => {
    if (!userData) throw new Error('userData is null');

    await executeThirdPartyAssociationQuery({
      provider: userData.provider,
      providerToken: userData.providerToken,
    });

    onSuccess({
      queryParams: {
        provider: userData.provider,
        ...(userFlow === SignupFlow.ContentHomeClub && { userFlow }),
        ...(userFlow === SignupFlow.Referral && {
          [QueryParam.ReferralAccountExists]: '',
        }),
      },
    });

    handleModalClose();
  }, [userData]);

  const performThirdPartyLogin = usePerformThirdPartyLogin({
    userFlow,
    referredBy,
    setCommonError,
    onSuccess: userData ? onPasswordLoginSuccess : onSuccess,
    initModalUserFlow,
  });

  const onModalClose = () => {
    handleModalClose();
    setUserData(null);
  };

  const passwordModal = userData && (
    <PasswordModal
      isOpen={isModalOpen}
      onModalClose={onModalClose}
      userFlow={userFlow}
      existentAssociations={userData.existentAssociations}
      onSuccess={onPasswordLoginSuccess}
      email={userData.email}
      provider={userData.provider}
      performLogin={performThirdPartyLogin}
      appleClientId={appleClientId}
      appleStateToken={appleStateToken}
    />
  );

  return { performThirdPartyLogin, passwordModal };
};
